import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Flex, Grid, Heading, Icon, Text, Button, Select, FormControl, FormLabel, FormHelperText,
  Table, Thead, Tbody, Tr, Th, Td, Badge, Checkbox, useToast,
  useColorModeValue, Tab, TabList, TabPanel, TabPanels, Tabs, Divider,
  NumberInput, NumberInputField, Alert, AlertIcon, AlertTitle, AlertDescription,
  Stat, StatLabel, StatNumber, StatHelpText, SimpleGrid, Input, Radio, RadioGroup,
  Stack, VStack, Tooltip, useDisclosure,
} from '@chakra-ui/react';
import {
  MdPeople, MdPerson, MdCalculate, MdAttachMoney, MdWork, MdExitToApp,
  MdCheckCircle, MdWarning, MdDownload, MdMonetizationOn,
} from 'react-icons/md';
import Card from 'components/card/Card';
import { getAllEmployees, getEmployeeById, terminateEmployee } from '../../../services/api';
import {
  getAllPayrollPeriods, calculatePayroll, getPayrollsByPeriod,
  downloadPayrollPdf, downloadSettlementReceiptPdf,
} from '../../../services/payroll.service';
import {
  advancedCalculatePayroll, previewPayroll, getAllPayrollTypes, getEmployeeConfig,
  getGlobalPayrollRates,
} from '../../../services/payroll-extended.service';
import PayrollPreviewModal from '../../../components/payroll/PayrollPreviewModal';

// ─────────────────────────────────────────────────────────
// Utilidades
// ─────────────────────────────────────────────────────────
const MONTHS = [
  'Enero','Febrero','Marzo','Abril','Mayo','Junio',
  'Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre',
];

const fmt = (n) =>
  new Intl.NumberFormat('es-UY', { style: 'currency', currency: 'UYU', minimumFractionDigits: 0 }).format(n || 0);

/** Body de ApiResponse tras httpClient.post (axios response.data). */
const extractPayrollPreviewFromResponse = (res) => {
  if (!res || typeof res !== 'object') return null;
  const p = res.payload?.data ?? res.payload;
  if (!p || typeof p !== 'object') return null;
  if (p.preview === true || p.grossSalary != null || p.netSalary != null || p.employeeName) return p;
  return null;
};

const diffYears = (from, to) => {
  const d1 = new Date(from), d2 = new Date(to);
  let years = d2.getFullYear() - d1.getFullYear();
  const m = d2.getMonth() - d1.getMonth();
  if (m < 0 || (m === 0 && d2.getDate() < d1.getDate())) years--;
  return years;
};

const diffMonths = (from, to) => {
  const d1 = new Date(from), d2 = new Date(to);
  return (d2.getFullYear() - d1.getFullYear()) * 12 + (d2.getMonth() - d1.getMonth());
};

const daysInMonth = (date) => {
  const d = new Date(date);
  return new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
};

/** Fecha de ingreso del empleado según distintos nombres de campo del API. */
const getEmployeeHireDateStr = (emp) => {
  if (!emp) return '';
  const nested = emp.employee && typeof emp.employee === 'object' ? emp.employee : null;
  const raw =
    emp.startDate ??
    emp.start_date ??
    emp.hireDate ??
    emp.hire_date ??
    nested?.startDate ??
    nested?.start_date ??
    nested?.hireDate ??
    nested?.hire_date ??
    null;
  if (raw == null || raw === '') return '';
  const s = String(raw).trim();
  const ymd = s.match(/^(\d{4}-\d{2}-\d{2})/);
  if (ymd) return ymd[1];
  const tryD = new Date(s);
  if (!Number.isNaN(tryD.getTime())) {
    const y = tryD.getFullYear();
    const m = String(tryD.getMonth() + 1).padStart(2, '0');
    const d = String(tryD.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  return '';
};

/**
 * Días trabajados en el mes del cese: desde el 1º de ese mes (o la fecha de ingreso si ingresó después)
 * hasta la fecha de egreso/renuncia inclusive. Sin fecha de ingreso: del 1 al día de egreso.
 */
const workedDaysInExitMonthInclusive = (hireDateStr, exitDateStr) => {
  if (!exitDateStr) return '';
  const parseLocal = (s) => {
    const parts = String(s).trim().slice(0, 10).split('-').map(Number);
    const [y, m, d] = parts;
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d);
  };
  const exit = parseLocal(exitDateStr);
  if (!exit || Number.isNaN(exit.getTime())) return '';
  const firstOfExitMonth = new Date(exit.getFullYear(), exit.getMonth(), 1);
  const hireRaw = hireDateStr != null && String(hireDateStr).trim() !== '' ? hireDateStr : null;
  const hire = hireRaw ? parseLocal(hireRaw) : null;
  if (!hire || Number.isNaN(hire.getTime())) {
    return exit.getDate();
  }
  const from = hire > firstOfExitMonth ? hire : firstOfExitMonth;
  if (from > exit) return 0;
  const diffDays = Math.round((exit.getTime() - from.getTime()) / 86400000) + 1;
  return Math.max(0, diffDays);
};

const LIQUIDACION_DIAS_LICENCIA_ANUAL = 20;
const LIQUIDACION_BASE_MES = 30;

const roundMoneyUpLiquidacion = (n) => {
  if (!Number.isFinite(n)) return 0;
  const s = n < 0 ? -1 : 1;
  const a = Math.abs(n);
  return s * (Math.ceil(a * 100 - 1e-9) / 100);
};

const parseLocalDateLiquidacion = (s) => {
  const parts = String(s).trim().slice(0, 10).split('-').map(Number);
  const [y, m, d] = parts;
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

// Antigüedad en años enteros al día de referencia.
const diffWholeYearsLiquidacion = (hireD, refD) => {
  if (!hireD || !refD) return 0;
  let years = refD.getFullYear() - hireD.getFullYear();
  const mDiff = refD.getMonth() - hireD.getMonth();
  if (mDiff < 0 || (mDiff === 0 && refD.getDate() < hireD.getDate())) years -= 1;
  return Math.max(0, years);
};

const daysInclusiveLiquidacion = (fromD, toD) => {
  if (!fromD || !toD || toD < fromD) return 0;
  const ms = toD.getTime() - fromD.getTime();
  return Math.floor(ms / 86400000) + 1;
};

// Base anual licencia: 20 días + 1 cada 4 años adicionales a partir de 5 años.
const annualLicenseBaseDaysLiquidacion = (hireD, refD) => {
  const years = diffWholeYearsLiquidacion(hireD, refD);
  if (years < 5) return LIQUIDACION_DIAS_LICENCIA_ANUAL;
  const extra = Math.floor((years - 5) / 4);
  return LIQUIDACION_DIAS_LICENCIA_ANUAL + Math.max(0, extra);
};

const diffWholeAntiguedadMonthsJs = (fromStr, toStr) => {
  const hire = parseLocalDateLiquidacion(fromStr);
  const term = parseLocalDateLiquidacion(toStr);
  if (!hire || !term || term < hire) return 0;
  let totalMonths = (term.getFullYear() - hire.getFullYear()) * 12 + (term.getMonth() - hire.getMonth());
  if (term.getDate() < hire.getDate()) totalMonths -= 1;
  return Math.max(0, Math.trunc(totalMonths));
};

const sumMonthFractionsBase30Liquidacion = (rangeStart, rangeEnd, capMonths = 6) => {
  if (!rangeStart || !rangeEnd || rangeStart > rangeEnd) return 0;
  let total = 0;
  const startDay = new Date(rangeStart.getFullYear(), rangeStart.getMonth(), rangeStart.getDate());
  let cur = new Date(startDay.getFullYear(), startDay.getMonth(), 1);
  while (cur <= rangeEnd) {
    const y = cur.getFullYear();
    const mo = cur.getMonth();
    const monthStart = new Date(y, mo, 1);
    const monthEnd = new Date(y, mo + 1, 0);
    const segStart = monthStart < startDay ? startDay : monthStart;
    const segEnd = monthEnd > rangeEnd ? rangeEnd : monthEnd;
    if (segStart <= segEnd) {
      const days = Math.floor((segEnd.getTime() - segStart.getTime()) / 86400000) + 1;
      total += Math.min(days / LIQUIDACION_BASE_MES, 1);
    }
    cur = new Date(y, mo + 1, 1);
  }
  return Math.min(total, capMonths);
};

/** Semestre del SAC vigente a la fecha de egreso: dic–may / jun–nov. */
const getTerminationSACSemesterBounds = (endD) => {
  const y = endD.getFullYear();
  const m = endD.getMonth();
  if (m <= 5) {
    return { semStart: new Date(y - 1, 11, 1), semEnd: new Date(y, 4, 31) };
  }
  return { semStart: new Date(y, 5, 1), semEnd: new Date(y, 10, 30) };
};

const normalizeEmployeesArray = (res) => {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.payload)) return res.payload;
  if (Array.isArray(res?.payload?.data)) return res.payload.data;
  if (Array.isArray(res?.payload?.employees)) return res.payload.employees;
  if (Array.isArray(res?.data)) return res.data;
  return [];
};

const unwrapEmployeeApiPayload = (data) => {
  if (!data || typeof data !== 'object') return null;
  return data.payload?.employee ?? data.payload ?? data.employee ?? data;
};

// ─────────────────────────────────────────────────────────
// Tab 1: Liquidación Masiva
// ─────────────────────────────────────────────────────────
function MasivaTab() {
  const toast = useToast();
  const [periods, setPeriods] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [payrollsExistentes, setPayrollsExistentes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);

  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const bgRow = useColorModeValue('gray.50', 'whiteAlpha.50');
  const existsColor = useColorModeValue('green.600', 'green.300');
  const statBlueBg = useColorModeValue('blue.50', 'blue.900');
  const statGreenBg = useColorModeValue('green.50', 'green.900');
  const statOrangeBg = useColorModeValue('orange.50', 'orange.900');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [empRes, perRes] = await Promise.all([getAllEmployees(), getAllPayrollPeriods()]);
      const emps =
        empRes?.payload?.employees ||
        empRes?.payload?.data ||
        empRes?.data?.payload?.data ||
        (Array.isArray(empRes?.data) ? empRes.data : null) ||
        (Array.isArray(empRes) ? empRes : []);
      const pers = perRes?.payload?.periods || perRes?.payload?.data || [];
      setEmployees(emps);
      setPeriods(pers);
    } catch {
      toast({ title: 'Error al cargar datos', status: 'error', duration: 3000 });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!selectedPeriod) { setPayrollsExistentes([]); setSelectedIds([]); return; }
    getPayrollsByPeriod(selectedPeriod).then((res) => {
      const data = res.data?.payload?.data || res.payload?.data || [];
      setPayrollsExistentes(data);
      const existingIds = new Set(data.map((p) => p.employee_id));
      setSelectedIds(employees.filter((e) => !existingIds.has(e.id)).map((e) => e.id));
    }).catch(() => {});
  }, [selectedPeriod, employees]);

  const toggleAll = (checked) =>
    setSelectedIds(checked ? employees.map((e) => e.id) : []);

  const toggle = (id) =>
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  const existingMap = Object.fromEntries(
    payrollsExistentes.map((p) => [p.employee_id, p]),
  );

  const handleGenerate = async (overwrite = false) => {
    if (!selectedPeriod) {
      toast({ title: 'Seleccioná un período', status: 'warning', duration: 3000 });
      return;
    }
    if (selectedIds.length === 0) {
      toast({ title: 'Seleccioná al menos un empleado', status: 'warning', duration: 3000 });
      return;
    }
    setGenerating(true);
    try {
      const res = await calculatePayroll({ periodId: Number(selectedPeriod), employeeIds: selectedIds }, overwrite);
      const payload = res.data?.payload || res.payload || {};
      const summary = payload.summary || {};
      const hasErrors = summary.errors > 0;
      toast({
        title: summary.success > 0 ? 'Liquidaciones generadas' : 'Sin nuevas liquidaciones',
        description: res.data?.message || res.message || `${summary.success} generados, ${summary.skipped} omitidos, ${summary.errors} con error`,
        status: hasErrors ? 'warning' : summary.success > 0 ? 'success' : 'info',
        duration: 6000,
        isClosable: true,
      });
      const updated = await getPayrollsByPeriod(selectedPeriod);
      const data = updated.data?.payload?.data || updated.payload?.data || [];
      setPayrollsExistentes(data);
      setSelectedIds([]);
    } catch (err) {
      toast({
        title: 'Error al generar',
        description: err?.response?.data?.message || 'Ocurrió un error',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setGenerating(false);
    }
  };

  const handleDownloadPdf = async (payrollId) => {
    try {
      const blob = await downloadPayrollPdf(payrollId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `recibo-${payrollId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast({ title: 'Error al descargar PDF', status: 'error', duration: 3000 });
    }
  };

  if (loading) return null;

  const pendingEmployees = employees.filter((e) => !existingMap[e.id]);
  const doneEmployees = employees.filter((e) => existingMap[e.id]);

  return (
    <Box>
      <Grid templateColumns={{ base: '1fr', md: '320px 1fr' }} gap={6}>
        {/* Panel izquierdo: configuración */}
        <Card p={5}>
          <Heading size="sm" mb={4}>Configuración</Heading>
          <FormControl mb={4} isRequired>
            <FormLabel fontSize="sm">Período de Liquidación</FormLabel>
            <Select
              placeholder="Seleccionar período"
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              size="sm"
            >
              {periods.map((p) => (
                <option key={p.id} value={p.id}>
                  {MONTHS[p.month - 1]} {p.year}
                </option>
              ))}
            </Select>
          </FormControl>

          {selectedPeriod && (
            <>
              <Divider mb={4} />
              <Flex justify="space-between" align="center" mb={2}>
                <Text fontSize="sm" fontWeight="600">
                  Empleados ({employees.length})
                </Text>
                <Checkbox
                  isChecked={selectedIds.length === employees.length}
                  isIndeterminate={selectedIds.length > 0 && selectedIds.length < employees.length}
                  onChange={(e) => toggleAll(e.target.checked)}
                  size="sm"
                >
                  <Text fontSize="xs">Todos</Text>
                </Checkbox>
              </Flex>
              <Box maxH="300px" overflowY="auto">
                {employees.map((emp) => {
                  const exists = !!existingMap[emp.id];
                  return (
                    <Flex
                      key={emp.id}
                      align="center"
                      justify="space-between"
                      py={1}
                      px={2}
                      bg={exists ? bgRow : 'transparent'}
                      borderRadius="md"
                      mb={1}
                    >
                      <Checkbox
                        isChecked={selectedIds.includes(emp.id)}
                        onChange={() => toggle(emp.id)}
                        size="sm"
                      >
                        <Text fontSize="xs" noOfLines={1}>
                          {emp.person?.name} {emp.person?.lastName}
                        </Text>
                      </Checkbox>
                      {exists && (
                        <Tooltip label="Ya liquidado">
                          <Icon as={MdCheckCircle} color={existsColor} boxSize={4} />
                        </Tooltip>
                      )}
                    </Flex>
                  );
                })}
              </Box>
              <Divider my={4} />
              <Button
                colorScheme="blue"
                w="full"
                leftIcon={<MdCalculate />}
                onClick={handleGenerate}
                isDisabled={selectedIds.length === 0}
              >
                Generar {selectedIds.length > 0 ? `(${selectedIds.length})` : ''} Recibos
              </Button>
            </>
          )}
        </Card>

        {/* Panel derecho: resultado */}
        <Box>
          {!selectedPeriod ? (
            <Flex align="center" justify="center" h="full" minH="200px">
              <Text color="gray.400">Seleccioná un período para ver el estado</Text>
            </Flex>
          ) : (
            <>
              <SimpleGrid columns={{ base: 1, sm: 3 }} spacing={4} mb={4}>
                <Stat bg={statBlueBg} p={4} borderRadius="lg">
                  <StatLabel fontSize="xs">Total empleados</StatLabel>
                  <StatNumber>{employees.length}</StatNumber>
                  <StatHelpText>con sueldo configurado</StatHelpText>
                </Stat>
                <Stat bg={statGreenBg} p={4} borderRadius="lg">
                  <StatLabel fontSize="xs">Liquidados</StatLabel>
                  <StatNumber color="green.500">{doneEmployees.length}</StatNumber>
                  <StatHelpText>recibos generados</StatHelpText>
                </Stat>
                <Stat bg={statOrangeBg} p={4} borderRadius="lg">
                  <StatLabel fontSize="xs">Pendientes</StatLabel>
                  <StatNumber color="orange.500">{pendingEmployees.length}</StatNumber>
                  <StatHelpText>sin liquidar</StatHelpText>
                </Stat>
              </SimpleGrid>

              {doneEmployees.length > 0 && (
                <Card p={4}>
                  <Heading size="sm" mb={3}>Recibos generados</Heading>
                  <Box overflowX="auto">
                    <Table size="sm" variant="simple">
                      <Thead>
                        <Tr>
                          <Th>Empleado</Th>
                          <Th isNumeric>Bruto</Th>
                          <Th isNumeric>Deducciones</Th>
                          <Th isNumeric>Neto</Th>
                          <Th>Estado</Th>
                          <Th></Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {doneEmployees.map((emp) => {
                          const pr = existingMap[emp.id];
                          return (
                            <Tr key={emp.id}>
                              <Td fontSize="sm">{emp.person?.name} {emp.person?.lastName}</Td>
                              <Td isNumeric fontSize="sm">{fmt(pr?.gross_salary)}</Td>
                              <Td isNumeric fontSize="sm" color="red.500">-{fmt(pr?.total_deductions)}</Td>
                              <Td isNumeric fontSize="sm" fontWeight="700" color="green.500">{fmt(pr?.net_salary)}</Td>
                              <Td>
                                <Badge colorScheme={pr?.status === 'PAID' ? 'green' : pr?.status === 'APPROVED' ? 'blue' : 'yellow'} fontSize="xs">
                                  {pr?.status || '-'}
                                </Badge>
                              </Td>
                              <Td>
                                <Button size="xs" leftIcon={<MdDownload />} onClick={() => handleDownloadPdf(pr?.id)}>
                                  PDF
                                </Button>
                              </Td>
                            </Tr>
                          );
                        })}
                      </Tbody>
                    </Table>
                  </Box>
                </Card>
              )}
            </>
          )}
        </Box>
      </Grid>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────
// Tab 2: Liquidación Individual
// ─────────────────────────────────────────────────────────
function IndividualTab() {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [periods, setPeriods] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  /** JSON.stringify(buildPayload()) de la última previsualización exitosa; guardar solo si coincide. */
  const previewPayloadSnapshot = useRef(null);

  // Lista dinámica de conceptos adicionales
  // type: 'bonus' | 'deduction'
  const [extras, setExtras] = useState([]);

  // Antigüedad (se auto-carga desde config del empleado)
  const [antiguedad, setAntiguedad] = useState({
    enabled: false, type: 'fixed', amountPerYear: 0, percentage: 0,
  });

  const [form, setForm] = useState({
    periodId: '', employeeId: '', payrollTypeId: '',
    workedDays: 30, absenceDays: 0,
    overtimeHours50: 0, overtimeHours100: 0,
    advanceDeduction: 0,
    observations: '',
  });

  useEffect(() => {
    setLoading(true);
    Promise.all([getAllEmployees(), getAllPayrollPeriods(), getAllPayrollTypes()])
      .then(([empRes, perRes, typRes]) => {
        const emps = normalizeEmployeesArray(empRes);
        setEmployees(emps);
        setPeriods(perRes?.payload?.periods || perRes?.payload?.data || []);
        setTypes(typRes?.payload?.data || typRes?.data?.payload?.data || []);
      })
      .catch(() => toast({ title: 'Error al cargar datos', status: 'error', duration: 3000 }))
      .finally(() => setLoading(false));
  }, [toast]);

  const set = (field, val) => setForm((prev) => ({ ...prev, [field]: val }));

  const selectedEmployee = employees.find((e) => e.id === Number(form.employeeId));

  // Calcular años de antigüedad del empleado seleccionado
  const today = new Date().toISOString().slice(0, 10);
  const yearsOfService = selectedEmployee?.startDate
    ? Math.max(diffYears(selectedEmployee.startDate, today), 0)
    : 0;

  // Cargar config de antigüedad al cambiar empleado
  useEffect(() => {
    if (!form.employeeId) return;
    getEmployeeConfig(form.employeeId).then((res) => {
      const cfg = res?.payload?.data || res?.data?.payload?.data || res?.payload;
      if (cfg) {
        setAntiguedad({
          enabled: !!cfg.antiguedadEnabled,
          type: cfg.antiguedadType || 'fixed',
          amountPerYear: cfg.antiguedadAmountPerYear || 0,
          percentage: cfg.antiguedadPercentage || 0,
        });
      }
    }).catch(() => {});
  }, [form.employeeId]);

  const antiguedadTotal = (() => {
    if (!antiguedad.enabled || yearsOfService === 0) return 0;
    if (antiguedad.type === 'fixed') return yearsOfService * Number(antiguedad.amountPerYear || 0);
    return yearsOfService * (Number(antiguedad.percentage || 0) / 100) * (selectedEmployee?.salary || 0);
  })();

  // Helpers lista dinámica
  const addExtra = (type) => setExtras((prev) => [...prev, { id: Date.now(), type, concept: '', amount: '' }]);
  const removeExtra = (id) => setExtras((prev) => prev.filter((e) => e.id !== id));
  const updateExtra = (id, field, val) => setExtras((prev) => prev.map((e) => e.id === id ? { ...e, [field]: val } : e));

  const totalBonos = extras.filter((e) => e.type === 'bonus').reduce((s, e) => s + Number(e.amount || 0), 0);
  const totalDeducciones = extras.filter((e) => e.type === 'deduction').reduce((s, e) => s + Number(e.amount || 0), 0);

  // Armar payload para backend
  const buildPayload = () => {
    const bonusAmount = totalBonos + antiguedadTotal;
    const otherDeductions = totalDeducciones;
    const parts = [];
    extras.forEach((e) => {
      if (e.concept) parts.push(`${e.type === 'bonus' ? '+' : '-'} ${e.concept}: ${fmt(e.amount)}`);
    });
    if (antiguedadTotal > 0) parts.push(`Antigüedad: ${yearsOfService} año(s) × ${antiguedad.type === 'fixed' ? fmt(antiguedad.amountPerYear) : antiguedad.percentage + '% del sueldo'}`);
    if (form.observations) parts.push(form.observations);
    return {
      periodId: Number(form.periodId),
      employeeId: Number(form.employeeId),
      payrollTypeId: form.payrollTypeId ? Number(form.payrollTypeId) : undefined,
      fonasaType: 'base',
      workedDays: Number(form.workedDays),
      absenceDays: Number(form.absenceDays),
      overtimeHours50: Number(form.overtimeHours50),
      overtimeHours100: Number(form.overtimeHours100),
      bonusAmount,
      advanceDeduction: Number(form.advanceDeduction),
      otherDeductions,
      observations: parts.join(' | '),
      createdBy: 1,
    };
  };

  const handlePreview = async () => {
    if (!form.periodId || !form.employeeId) {
      toast({ title: 'Completá período y empleado', status: 'warning', duration: 3000 });
      return;
    }
    setCalculating(true);
    try {
      const payload = buildPayload();
      const res = await previewPayroll(payload);
      const preview = extractPayrollPreviewFromResponse(res);
      if (!preview) {
        toast({
          title: 'Vista previa no disponible',
          description: 'La respuesta del servidor no trajo los datos de liquidación. Revisá la consola o intentá de nuevo.',
          status: 'error',
          duration: 5000,
        });
        return;
      }
      setPreviewData(preview);
      previewPayloadSnapshot.current = JSON.stringify(payload);
      onOpen();
    } catch (err) {
      toast({
        title: 'Error al calcular',
        description: err?.response?.data?.message || err?.message || 'Verificá los datos',
        status: 'error', duration: 5000,
      });
    } finally {
      setCalculating(false);
    }
  };

  const handleSave = async (overwrite = false) => {
    const payload = buildPayload();
    if (previewPayloadSnapshot.current == null || previewPayloadSnapshot.current !== JSON.stringify(payload)) {
      toast({
        title: 'Previsualizá de nuevo',
        description: 'Los datos cambiaron o no hay una vista previa válida. Usá «Previsualizar» y recién después guardá desde el modal.',
        status: 'warning',
        duration: 5000,
      });
      return;
    }
    setCalculating(true);
    try {
      await advancedCalculatePayroll(payload, overwrite);
      toast({ title: 'Recibo generado correctamente', status: 'success', duration: 4000 });
      previewPayloadSnapshot.current = null;
      setPreviewData(null);
      onClose();
    } catch (err) {
      toast({
        title: 'Error al guardar',
        description: err?.response?.data?.message || 'Ocurrió un error',
        status: 'error', duration: 5000,
      });
    } finally {
      setCalculating(false);
    }
  };

  const handleModalClose = () => {
    onClose();
  };

  if (loading) return null;

  return (
    <Box>
      <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={6}>
        <Card p={5}>
          <Heading size="sm" mb={4}>Datos del Empleado</Heading>
          <VStack spacing={4}>
            <FormControl isRequired>
              <FormLabel fontSize="sm">Período</FormLabel>
              <Select size="sm" placeholder="Seleccionar período" value={form.periodId} onChange={(e) => set('periodId', e.target.value)}>
                {periods.map((p) => <option key={p.id} value={p.id}>{MONTHS[p.month - 1]} {p.year}</option>)}
              </Select>
            </FormControl>
            <FormControl isRequired>
              <FormLabel fontSize="sm">Empleado</FormLabel>
              <Select size="sm" placeholder="Seleccionar empleado" value={form.employeeId} onChange={(e) => set('employeeId', e.target.value)}>
                {employees.map((e) => <option key={e.id} value={e.id}>{e.person?.name} {e.person?.lastName}</option>)}
              </Select>
            </FormControl>
            {selectedEmployee && (
              <Alert status="info" borderRadius="md" fontSize="sm">
                <AlertIcon />
                Sueldo base: <strong style={{ marginLeft: 4 }}>{fmt(selectedEmployee.salary)}</strong>
              </Alert>
            )}
            <FormControl>
              <FormLabel fontSize="sm">Tipo de Liquidación</FormLabel>
              <Select size="sm" placeholder="Tipo por defecto" value={form.payrollTypeId} onChange={(e) => set('payrollTypeId', e.target.value)}>
                {types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </Select>
            </FormControl>
          </VStack>
        </Card>

        <Card p={5}>
          <Heading size="sm" mb={4}>Detalles del Período</Heading>
          <Grid templateColumns="1fr 1fr" gap={3}>
            <FormControl>
              <FormLabel fontSize="xs">Días trabajados</FormLabel>
              <NumberInput size="sm" min={0} max={31} value={form.workedDays} onChange={(v) => set('workedDays', v)}>
                <NumberInputField />
              </NumberInput>
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs">Días de ausencia</FormLabel>
              <NumberInput size="sm" min={0} value={form.absenceDays} onChange={(v) => set('absenceDays', v)}>
                <NumberInputField />
              </NumberInput>
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs">Horas extras 50%</FormLabel>
              <NumberInput size="sm" min={0} value={form.overtimeHours50} onChange={(v) => set('overtimeHours50', v)}>
                <NumberInputField />
              </NumberInput>
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs">Horas extras 100%</FormLabel>
              <NumberInput size="sm" min={0} value={form.overtimeHours100} onChange={(v) => set('overtimeHours100', v)}>
                <NumberInputField />
              </NumberInput>
            </FormControl>

            {/* Adelanto */}
            <FormControl>
              <FormLabel fontSize="xs">Adelanto ($)</FormLabel>
              <NumberInput size="sm" min={0} value={form.advanceDeduction} onChange={(v) => set('advanceDeduction', v)}>
                <NumberInputField />
              </NumberInput>
            </FormControl>
          </Grid>

          {/* Antigüedad — se carga automáticamente de la config del empleado */}
          {antiguedad.enabled && selectedEmployee && (
            <>
              <Divider my={4} />
              <Alert status="info" fontSize="xs" borderRadius="md">
                <AlertIcon />
                <Box>
                  <Text fontWeight="600">Antigüedad configurada para este empleado</Text>
                  <Text>
                    {yearsOfService} año(s) de servicio
                    {antiguedad.type === 'fixed'
                      ? ` × $${antiguedad.amountPerYear}/año`
                      : ` × ${antiguedad.percentage}% del sueldo/año`}
                    {' → '}
                    <strong>{fmt(antiguedadTotal)}</strong>
                  </Text>
                </Box>
              </Alert>
            </>
          )}

          {/* Lista dinámica de bonificaciones adicionales */}
          <Divider my={4} />
          <Flex align="center" justify="space-between" mb={2}>
            <Text fontSize="sm" fontWeight="600">Bonificaciones adicionales</Text>
            <Button size="xs" colorScheme="green" leftIcon={<Text>+</Text>} onClick={() => addExtra('bonus')}>
              Agregar
            </Button>
          </Flex>
          {extras.filter((e) => e.type === 'bonus').length === 0 && (
            <Text fontSize="xs" color="gray.400" mb={2}>Sin bonificaciones adicionales</Text>
          )}
          {extras.filter((e) => e.type === 'bonus').map((e) => (
            <Flex key={e.id} gap={2} mb={2} align="center">
              <Input
                size="sm" placeholder="Concepto (ej: Premio, viáticos...)"
                value={e.concept} onChange={(ev) => updateExtra(e.id, 'concept', ev.target.value)}
                flex={2}
              />
              <NumberInput size="sm" min={0} value={e.amount} onChange={(v) => updateExtra(e.id, 'amount', v)} flex={1}>
                <NumberInputField placeholder="$" />
              </NumberInput>
              <Button size="xs" colorScheme="red" variant="ghost" onClick={() => removeExtra(e.id)}>✕</Button>
            </Flex>
          ))}
          {totalBonos > 0 && <Text fontSize="xs" color="green.600" textAlign="right" mb={2}>Total bonos: {fmt(totalBonos)}</Text>}

          {/* Lista dinámica de deducciones adicionales */}
          <Divider my={3} />
          <Flex align="center" justify="space-between" mb={2}>
            <Text fontSize="sm" fontWeight="600">Deducciones adicionales</Text>
            <Button size="xs" colorScheme="red" leftIcon={<Text>+</Text>} onClick={() => addExtra('deduction')}>
              Agregar
            </Button>
          </Flex>
          {extras.filter((e) => e.type === 'deduction').length === 0 && (
            <Text fontSize="xs" color="gray.400" mb={2}>Sin deducciones adicionales</Text>
          )}
          {extras.filter((e) => e.type === 'deduction').map((e) => (
            <Flex key={e.id} gap={2} mb={2} align="center">
              <Input
                size="sm" placeholder="Concepto (ej: Embargo, uniforme...)"
                value={e.concept} onChange={(ev) => updateExtra(e.id, 'concept', ev.target.value)}
                flex={2}
              />
              <NumberInput size="sm" min={0} value={e.amount} onChange={(v) => updateExtra(e.id, 'amount', v)} flex={1}>
                <NumberInputField placeholder="$" />
              </NumberInput>
              <Button size="xs" colorScheme="red" variant="ghost" onClick={() => removeExtra(e.id)}>✕</Button>
            </Flex>
          ))}
          {totalDeducciones > 0 && <Text fontSize="xs" color="red.600" textAlign="right" mb={2}>Total deducciones: {fmt(totalDeducciones)}</Text>}
        </Card>
      </Grid>

      <Flex justify="flex-end" align="center" mt={4} gap={3} flexWrap="wrap">
        <Text fontSize="sm" color="gray.500" mr="auto" maxW="md">
          Primero previsualizá la liquidación. El guardado solo está disponible en el modal, después de ver el detalle.
        </Text>
        <Button
          colorScheme="blue"
          leftIcon={<MdCalculate />}
          onClick={handlePreview}
          isDisabled={!form.periodId || !form.employeeId}
        >
          Previsualizar
        </Button>
      </Flex>

      <PayrollPreviewModal
        isOpen={isOpen}
        onClose={handleModalClose}
        previewData={previewData}
        onConfirm={() => handleSave(previewData?.hasExisting === true)}
        loading={calculating}
      />
    </Box>
  );
}

// ─────────────────────────────────────────────────────────
// Tab: Liquidación Aguinaldo (SAC semestral — tipo AGUINALDO en backend)
// ─────────────────────────────────────────────────────────

function AguinaldoTab() {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [periods, setPeriods] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [previewData, setPreviewData] = useState(null);

  const [form, setForm] = useState({
    periodId: '',
    employeeId: '',
    fonasaType: 'base',
    aguinaldoAmountOverride: '',
    observations: '',
  });

  const setField = (field, val) => setForm((prev) => ({ ...prev, [field]: val }));

  useEffect(() => {
    setLoading(true);
    Promise.all([getAllEmployees(), getAllPayrollPeriods(), getAllPayrollTypes()])
      .then(([empRes, perRes, typRes]) => {
        const emps =
          empRes?.payload?.employees ||
          empRes?.payload?.data ||
          empRes?.data?.payload?.data ||
          (Array.isArray(empRes?.data) ? empRes.data : null) ||
          (Array.isArray(empRes) ? empRes : []);
        const pers = perRes?.payload?.periods || perRes?.payload?.data || [];
        const tys = typRes?.payload?.data || typRes?.data?.payload?.data || [];
        setEmployees(emps);
        setPeriods(pers);
        setTypes(tys);
      })
      .catch(() => toast({ title: 'Error al cargar datos', status: 'error', duration: 3000 }))
      .finally(() => setLoading(false));
  }, [toast]);

  const aguinaldoType = types.find(
    (t) => String(t.code || '').toUpperCase() === 'AGUINALDO',
  );
  const semesterPeriods = periods
    .filter((p) => p.month === 6 || p.month === 12)
    .sort((a, b) => (b.year !== a.year ? b.year - a.year : b.month - a.month));

  const selectedEmployee = employees.find((e) => e.id === Number(form.employeeId));

  const buildPayload = () => {
    const base = {
      periodId: Number(form.periodId),
      employeeId: Number(form.employeeId),
      payrollTypeId: aguinaldoType.id,
      fonasaType: form.fonasaType,
      workedDays: 30,
      absenceDays: 0,
      bonusAmount: 0,
      advanceDeduction: 0,
      otherDeductions: 0,
      observations: form.observations || '',
      createdBy: 1,
    };
    if (form.aguinaldoAmountOverride !== '' && form.aguinaldoAmountOverride != null) {
      base.aguinaldoAmount = parseFloat(form.aguinaldoAmountOverride) || 0;
    }
    return base;
  };

  const handlePreview = async () => {
    if (!aguinaldoType) {
      toast({
        title: 'Falta tipo de liquidación',
        description: 'Debe existir un tipo de nómina con código AGUINALDO en el sistema.',
        status: 'warning',
        duration: 5000,
      });
      return;
    }
    if (!form.periodId || !form.employeeId) {
      toast({ title: 'Seleccioná período (junio o diciembre) y empleado', status: 'warning', duration: 3000 });
      return;
    }
    setCalculating(true);
    try {
      const res = await previewPayroll(buildPayload());
      setPreviewData(res.data?.payload?.data || res.payload?.data || res.data?.payload);
      onOpen();
    } catch (err) {
      toast({
        title: 'Error al calcular',
        description: err?.response?.data?.message || 'Verificá fechas de ingreso y período semestral',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setCalculating(false);
    }
  };

  const handleSave = async (overwrite = false) => {
    setCalculating(true);
    try {
      await advancedCalculatePayroll(buildPayload(), overwrite);
      toast({ title: 'Liquidación de aguinaldo guardada', status: 'success', duration: 4000 });
      onClose();
    } catch (err) {
      toast({
        title: 'Error al guardar',
        description: err?.response?.data?.message || 'Ocurrió un error',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setCalculating(false);
    }
  };

  if (loading) return null;

  return (
    <Box>
      {!aguinaldoType && (
        <Alert status="error" mb={4} borderRadius="md" fontSize="sm">
          <AlertIcon />
          No hay tipo de liquidación con código <strong>AGUINALDO</strong>. Crearlo en nómina o ejecutar migraciones.
        </Alert>
      )}

      <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={6}>
        <Card p={5}>
          <Heading size="sm" mb={4}>Datos</Heading>
          <VStack spacing={4}>
            <FormControl isRequired>
              <FormLabel fontSize="sm">Período (Junio o Diciembre)</FormLabel>
              <Select
                size="sm"
                placeholder="Seleccionar período SAC"
                value={form.periodId}
                onChange={(e) => setField('periodId', e.target.value)}
              >
                {semesterPeriods.map((p) => (
                  <option key={p.id} value={p.id}>
                    {MONTHS[p.month - 1]} {p.year}
                  </option>
                ))}
              </Select>
            </FormControl>
            {semesterPeriods.length === 0 && (
              <Text fontSize="xs" color="gray.500">No hay períodos de Junio/Diciembre cargados.</Text>
            )}

            <FormControl isRequired>
              <FormLabel fontSize="sm">Empleado</FormLabel>
              <Select
                size="sm"
                placeholder="Seleccionar empleado"
                value={form.employeeId}
                onChange={(e) => setField('employeeId', e.target.value)}
              >
                {employees.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.person?.name} {e.person?.lastName}
                  </option>
                ))}
              </Select>
            </FormControl>

            {selectedEmployee && (
              <Alert status="info" borderRadius="md" fontSize="xs">
                <AlertIcon />
                <Box>
                  <Text>
                    Ingreso: <strong>{getEmployeeHireDateStr(selectedEmployee) || '—'}</strong>
                  </Text>
                  <Text mt={1}>
                    Sueldo (ficha): <strong>{fmt(selectedEmployee.salary)}</strong>
                    {' — '}
                    el importe liquidado usa la <strong>configuración de nómina</strong> del empleado.
                  </Text>
                </Box>
              </Alert>
            )}

            <FormControl>
              <FormLabel fontSize="sm">Tipo FONASA (aportes)</FormLabel>
              <Select
                size="sm"
                value={form.fonasaType}
                onChange={(e) => setField('fonasaType', e.target.value)}
              >
                <option value="base">FONASA Base (4.5%)</option>
                <option value="with_spouse">FONASA con Cónyuge (6.0%)</option>
                <option value="one_child">FONASA con 1 Hijo (5.0%)</option>
                <option value="two_children">FONASA con 2 Hijos (6.0%)</option>
                <option value="three_or_more_children">FONASA con 3 o más Hijos (8.0%)</option>
                <option value="with_spouse_and_children">FONASA con Cónyuge e Hijos (8.0%)</option>
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm">Monto aguinaldo manual (opcional)</FormLabel>
              <NumberInput
                size="sm"
                min={0}
                value={form.aguinaldoAmountOverride}
                onChange={(v) => setField('aguinaldoAmountOverride', v)}
              >
                <NumberInputField placeholder="Vacío = cálculo automático" />
              </NumberInput>
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm">Observaciones</FormLabel>
              <Input
                size="sm"
                value={form.observations}
                onChange={(e) => setField('observations', e.target.value)}
                placeholder="Opcional"
              />
            </FormControl>
          </VStack>
        </Card>

        <Card p={5}>
          <Heading size="sm" mb={2}>Pasos</Heading>
          <Text fontSize="sm" color="gray.600" mb={4}>
            Previsualizá la liquidación para revisar haberes (aguinaldo), deducciones y neto. Guardá solo cuando esté conforme.
          </Text>
          <Button
            colorScheme="teal"
            leftIcon={<MdCalculate />}
            onClick={handlePreview}
            isDisabled={!aguinaldoType || !form.periodId || !form.employeeId}
          >
            Previsualizar y guardar
          </Button>
        </Card>
      </Grid>

      {previewData && (
        <PayrollPreviewModal
          isOpen={isOpen}
          onClose={onClose}
          previewData={previewData}
          onConfirm={() => handleSave(previewData?.hasExisting === true)}
          loading={calculating}
        />
      )}
    </Box>
  );
}

// ─────────────────────────────────────────────────────────
// Tab 3: Despidos y Renuncias (leyes uruguayas)
// ─────────────────────────────────────────────────────────

const MOTIVOS = [
  { value: 'despido_sin_causa', label: 'Despido sin expresión de causa', indemniza: true },
  { value: 'despido_indirecto', label: 'Despido indirecto', indemniza: true },
  { value: 'reduccion_personal', label: 'Despido por reducción de personal', indemniza: true },
  { value: 'renuncia', label: 'Renuncia voluntaria', indemniza: false },
  { value: 'mutuo_acuerdo', label: 'Mutuo acuerdo', indemniza: false },
  { value: 'vencimiento_plazo', label: 'Vencimiento de contrato a término', indemniza: false },
];

function calcularLiquidacion(salary, startDate, endDate, motivo, diasTrabajadosUltimoMes, diasLicNGozadaAnteriores) {
  if (!salary || !startDate || !endDate) return null;

  const salaryNum = Number(salary);
  const totalYears = diffYears(startDate, endDate);
  const totalMonths = diffWholeAntiguedadMonthsJs(startDate, endDate);

  const endD = parseLocalDateLiquidacion(endDate);
  if (!endD) return null;
  const hireD = parseLocalDateLiquidacion(startDate);

  let diasTrabajados;
  if (diasTrabajadosUltimoMes === '' || diasTrabajadosUltimoMes == null) {
    diasTrabajados = workedDaysInExitMonthInclusive(startDate, endDate);
    if (diasTrabajados === '') diasTrabajados = endD.getDate();
    else diasTrabajados = Number(diasTrabajados);
  } else {
    const n = Number(diasTrabajadosUltimoMes);
    diasTrabajados = Number.isFinite(n) ? n : endD.getDate();
  }
  diasTrabajados = Math.max(0, Math.min(LIQUIDACION_BASE_MES, diasTrabajados));

  const sueldoProporcional =
    diasTrabajados >= LIQUIDACION_BASE_MES
      ? salaryNum
      : roundMoneyUpLiquidacion((salaryNum / LIQUIDACION_BASE_MES) * diasTrabajados);

  const { semStart, semEnd } = getTerminationSACSemesterBounds(endD);
  let intervalStart = semStart;
  if (hireD && hireD > intervalStart) intervalStart = hireD;
  let intervalEnd = semEnd;
  if (endD < intervalEnd) intervalEnd = endD;
  const aguinaldoFraccionMeses =
    intervalStart > intervalEnd ? 0 : sumMonthFractionsBase30Liquidacion(intervalStart, intervalEnd, 6);
  const aguinaldoProporcional =
    intervalStart > intervalEnd ? 0 : roundMoneyUpLiquidacion((salaryNum * aguinaldoFraccionMeses) / 12);

  const sueldoDiarioLicencia = salaryNum / LIQUIDACION_BASE_MES; // sueldo/30
  const termYear = endD.getFullYear();
  const hireY = hireD ? hireD.getFullYear() : termYear;
  const hadPriorYears = Boolean(hireD) && hireY < termYear;

  // Año actual: proporcional desde el 1/1 hasta egreso.
  let diasLicenciaAnoActualNum = 0;
  if (hireD) {
    const yearStart = new Date(termYear, 0, 1);
    const proportionalStart = hireD > yearStart ? hireD : yearStart;
    const daysWorkedInYear = daysInclusiveLiquidacion(proportionalStart, endD);
    const baseDaysCurrentYear = annualLicenseBaseDaysLiquidacion(hireD, endD);
    const rawDays = (daysWorkedInYear / 365) * baseDaysCurrentYear;
    diasLicenciaAnoActualNum = Math.max(0, Math.ceil(rawDays));
  }
  const licenciaProporcionalAnoActual = roundMoneyUpLiquidacion(diasLicenciaAnoActualNum * sueldoDiarioLicencia);

  // Años anteriores: solo lo ingresado en “Días pendientes de años anteriores” (vacío = 0; “0” = 0).
  const rawPriorStr =
    diasLicNGozadaAnteriores == null || diasLicNGozadaAnteriores === ''
      ? ''
      : String(diasLicNGozadaAnteriores).trim();
  let diasLicNGozada = 0;
  if (rawPriorStr !== '') {
    const n = Number(rawPriorStr);
    if (Number.isFinite(n) && n >= 0) diasLicNGozada = Math.trunc(n);
  }

  const licenciaNGozadaAnteriores = roundMoneyUpLiquidacion(diasLicNGozada * sueldoDiarioLicencia);
  const vacationPayTotal = licenciaProporcionalAnoActual + licenciaNGozadaAnteriores;
  const salarioVacacionalTotal = roundMoneyUpLiquidacion(vacationPayTotal);
  const salarioVacacionalAnoActual = salarioVacacionalTotal;

  const motivoObj = MOTIVOS.find((m) => m.value === motivo);
  const correspondeIndemnizacion = motivoObj?.indemniza ?? false;
  const mesesRestIp = totalMonths % 12;
  const yearsEnterosIp = Math.floor(totalMonths / 12);
  let yearsComputables = yearsEnterosIp + (mesesRestIp > 6 ? 1 : 0);
  yearsComputables = Math.min(6, Math.max(yearsComputables, correspondeIndemnizacion ? 1 : 0));
  const indemnizacion = correspondeIndemnizacion
    ? roundMoneyUpLiquidacion(salaryNum * yearsComputables)
    : 0;

  let preaviso = 0;
  if (correspondeIndemnizacion) {
    const diasPrv = totalMonths < 6 ? 15 : 30;
    preaviso = roundMoneyUpLiquidacion((salaryNum / LIQUIDACION_BASE_MES) * diasPrv);
  }

  const subtotal =
    sueldoProporcional
    + aguinaldoProporcional
    + vacationPayTotal
    + salarioVacacionalTotal;
  const total = subtotal + indemnizacion + preaviso;

  return {
    sueldoProporcional,
    aguinaldoProporcional,
    licenciaProporcionalAnoActual,
    salarioVacacionalAnoActual,
    salarioVacacionalTotal,
    licenciaNGozadaAnteriores,
    salarioVacacionalNGozado: licenciaNGozadaAnteriores,
    diasLicNGozada,
    hadPriorYears,
    indemnizacion,
    preaviso,
    subtotal,
    total,
    totalYears,
    totalMonths,
    diasLicenciaAnoActual: diasLicenciaAnoActualNum.toFixed(1),
    aniosParaIndemnizar: yearsComputables,
    correspondeIndemnizacion,
    mesesEnSemestre: Number(aguinaldoFraccionMeses.toFixed(2)),
    aguinaldoFraccionMeses,
  };
}

/**
 * Deducciones de egreso: BPS, FONASA y FRL solo sobre partidas gravadas (sueldo, aguinaldo, licencias).
 * Sin aportes: salario vacacional, IPD, sustitutivo de preaviso. IRPF en liquidación definitiva (tabla mensual).
 */
function mergeSimuladorDeduccionesEgreso(resultado, ratesPayload) {
  if (!resultado) return null;
  const bpsR = Number(ratesPayload?.bps_rate);
  const frlR = Number(ratesPayload?.frl_rate);
  const fonasaR = Number(ratesPayload?.fonasa_base_rate);
  const safeBps = Number.isFinite(bpsR) && bpsR >= 0 ? bpsR : 0.15;
  const safeFrl = Number.isFinite(frlR) && frlR >= 0 ? frlR : 0.001;
  const safeFonasa = Number.isFinite(fonasaR) && fonasaR >= 0 ? fonasaR : 0.045;

  const grav =
    (Number(resultado.sueldoProporcional) || 0)
    + (Number(resultado.aguinaldoProporcional) || 0)
    + (Number(resultado.licenciaProporcionalAnoActual) || 0)
    + (Number(resultado.licenciaNGozadaAnteriores) || 0);

  const noGrav =
    (Number(resultado.salarioVacacionalTotal) || 0)
    + (Number(resultado.indemnizacion) || 0)
    + (Number(resultado.preaviso) || 0);

  const bps = roundMoneyUpLiquidacion(grav * safeBps);
  const fonasa = roundMoneyUpLiquidacion(grav * safeFonasa);
  const frl = roundMoneyUpLiquidacion(grav * safeFrl);
  const irpf = 0;

  const deduccionesSim = [
    { concept: `Jubilación / BPS (${(safeBps * 100).toFixed(1)}%)`, amount: bps },
    { concept: `FONASA (${(safeFonasa * 100).toFixed(1)}%)`, amount: fonasa },
    { concept: `FRL (${(safeFrl * 100).toFixed(2)}%)`, amount: frl },
    { concept: 'IRPF (según liquidación definitiva en el período)', amount: irpf },
  ];

  const totalDeduccionesSim = bps + fonasa + frl + irpf;
  const brutoTotalSim = grav + noGrav;
  const netoLiquidacionSim = roundMoneyUpLiquidacion(brutoTotalSim - totalDeduccionesSim);

  return {
    ...resultado,
    haberesGravadosSim: grav,
    haberesNoGravadosSim: noGrav,
    brutoTotalSim,
    deduccionesSim,
    totalDeduccionesSim,
    netoLiquidacionSim,
  };
}

/** Arma el body para POST /payroll/receipt/settlement-pdf (liquidación final estimada). */
function buildFinalSettlementReceiptDto({
  empObj, resultado, motivo, startDate, endDate, bankAccount,
}) {
  const p = empObj?.person || {};
  const fullName = [
    p.name,
    p.secondName ?? p.second_name,
    p.lastName ?? p.last_name,
    p.maternalLastName ?? p.maternal_last_name,
  ].filter(Boolean).join(' ').trim() || '—';

  const doc = String(p.document ?? p.documentNumber ?? '').trim() || '—';
  const tipo = empObj?.employeeType ?? empObj?.employee_type;
  const category = tipo?.description || tipo?.name || '';
  const position = [empObj?.employeeTitle, empObj?.position].filter(Boolean).join(' — ');
  const motivoLabel = MOTIVOS.find((m) => m.value === motivo)?.label || motivo;

  const haberes = [
    { concept: 'Sueldo proporcional del mes', amount: Number(resultado.sueldoProporcional) || 0 },
    { concept: 'Aguinaldo proporcional', amount: Number(resultado.aguinaldoProporcional) || 0 },
    {
      concept: `Licencia proporcional año actual (${resultado.diasLicenciaAnoActual} días)`,
      amount: Number(resultado.licenciaProporcionalAnoActual) || 0,
    },
  ];

  if (resultado.hadPriorYears) {
    haberes.push({
      concept: `Licencia no gozada años anteriores (${resultado.diasLicNGozada} días)`,
      amount: Number(resultado.licenciaNGozadaAnteriores) || 0,
    });
  }
  haberes.push({
    concept: `Salario vacacional (${resultado.diasLicenciaAnoActual} + ${resultado.diasLicNGozada || 0} días equivalentes)`,
    amount: Number(resultado.salarioVacacionalTotal ?? resultado.salarioVacacionalAnoActual) || 0,
  });

  if (resultado.correspondeIndemnizacion && Number(resultado.indemnizacion) > 0) {
    haberes.push({
      concept: `Indemnización por despido (${resultado.aniosParaIndemnizar} sueldo(s))`,
      amount: Number(resultado.indemnizacion) || 0,
    });
  }
  if (Number(resultado.preaviso) > 0) {
    haberes.push({ concept: 'Sustitutivo de preaviso', amount: Number(resultado.preaviso) || 0 });
  }

  const deducciones = Array.isArray(resultado.deduccionesSim)
    ? resultado.deduccionesSim
      .filter((d) => d && Number(d.amount) > 0)
      .map((d) => ({ concept: String(d.concept || ''), amount: Number(d.amount) || 0 }))
    : [];

  return {
    documentTitle: 'Recibo de liquidación final',
    periodLabel: `Liquidación final — ${motivoLabel}`,
    periodRange: `${startDate} al ${endDate}`,
    employeeFullName: fullName,
    documentId: doc,
    documentTypeLabel: p.documentType?.description || undefined,
    startDate,
    position: position || undefined,
    category: category || undefined,
    haberes,
    deducciones,
    netSalary: Number(resultado.netoLiquidacionSim ?? resultado.total) || 0,
    paymentDate: endDate,
    bankAccount: bankAccount || null,
    observations:
      'Simulación: aportes sobre partidas gravadas (sueldo, aguinaldo, licencias). Sin aportes sobre salario vacacional, IPD y sustitutivo de preaviso. IRPF según liquidación definitiva.',
  };
}

function DespidosTab() {
  const [employees, setEmployees] = useState([]);
  const [selectedEmp, setSelectedEmp] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState(new Date().toISOString().slice(0, 10));
  const [motivo, setMotivo] = useState('despido_sin_causa');
  const [diasUltimoMes, setDiasUltimoMes] = useState('');
  const [diasLicNGozadaAnteriores, setDiasLicNGozadaAnteriores] = useState('');
  const [resultado, setResultado] = useState(null);
  const [loading, setLoading] = useState(false);
  const [registrando, setRegistrando] = useState(false);
  const [egresoRegistrado, setEgresoRegistrado] = useState(false);
  const [downloadingSettlementPdf, setDownloadingSettlementPdf] = useState(false);
  const toast = useToast();

  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const headerBg = useColorModeValue('gray.50', 'whiteAlpha.50');
  const statGrayBg = useColorModeValue('gray.50', 'whiteAlpha.50');
  const statBlueBg = useColorModeValue('blue.50', 'blue.900');
  const statGreenBg = useColorModeValue('green.50', 'green.900');

  useEffect(() => {
    getAllEmployees()
      .then((res) => setEmployees(normalizeEmployeesArray(res)))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedEmp) {
      setStartDate('');
      return;
    }
    let cancelled = false;
    const id = Number(selectedEmp);
    const fromList = employees.find((e) => Number(e.id) === id);

    const applyHire = (emp) => {
      if (cancelled) return;
      setStartDate(getEmployeeHireDateStr(emp) || '');
    };

    const quick = getEmployeeHireDateStr(fromList);
    if (quick) setStartDate(quick);

    getEmployeeById(id)
      .then((raw) => {
        if (cancelled) return;
        const emp = unwrapEmployeeApiPayload(raw);
        applyHire(emp || fromList);
      })
      .catch(() => applyHire(fromList));

    return () => {
      cancelled = true;
    };
  }, [selectedEmp, employees]);

  useEffect(() => {
    if (!endDate) return;
    const d = workedDaysInExitMonthInclusive(startDate, endDate);
    if (d !== '') setDiasUltimoMes(String(d));
  }, [startDate, endDate]);

  const empObj = employees.find((e) => Number(e.id) === Number(selectedEmp));

  const calcular = async () => {
    if (!selectedEmp || !startDate || !endDate) {
      toast({ title: 'Completá todos los campos requeridos', status: 'warning', duration: 3000 });
      return;
    }
    setLoading(true);
    setEgresoRegistrado(false);
    try {
      let ratesPayload = null;
      try {
        const gr = await getGlobalPayrollRates();
        ratesPayload = gr?.payload ?? null;
      } catch {
        ratesPayload = null;
      }
      const raw = calcularLiquidacion(
        empObj?.salary,
        startDate,
        endDate,
        motivo,
        diasUltimoMes,
        diasLicNGozadaAnteriores,
      );
      setResultado(mergeSimuladorDeduccionesEgreso(raw, ratesPayload));
    } finally {
      setLoading(false);
    }
  };

  const handleRegistrarEgreso = async () => {
    if (!selectedEmp || !endDate || !motivo) return;
    setRegistrando(true);
    try {
      await terminateEmployee(Number(selectedEmp), {
        terminationDate: endDate,
        terminationReason: MOTIVOS.find((m) => m.value === motivo)?.label || motivo,
        endDate,
      });
      setEgresoRegistrado(true);
      toast({
        title: 'Egreso registrado',
        description: `${empObj?.person?.name} ${empObj?.person?.lastName} fue dado de baja correctamente.`,
        status: 'success',
        duration: 5000,
      });
    } catch {
      toast({ title: 'Error al registrar egreso', status: 'error', duration: 4000 });
    } finally {
      setRegistrando(false);
    }
  };

  const handleDownloadSettlementPdf = async () => {
    if (!empObj || !resultado || !startDate || !endDate) return;
    setDownloadingSettlementPdf(true);
    let bankAccount = null;
    try {
      const cfgRes = await getEmployeeConfig(empObj.id);
      const cfg = cfgRes?.payload?.data || cfgRes?.data?.payload?.data;
      const raw = cfg?.bank_account ?? cfg?.bankAccount;
      bankAccount = raw != null && raw !== '' ? String(raw) : null;
    } catch {
      /* sin configuración de nómina o cuenta */
    }
    try {
      const dto = buildFinalSettlementReceiptDto({
        empObj,
        resultado,
        motivo,
        startDate,
        endDate,
        bankAccount,
      });
      const blob = await downloadSettlementReceiptPdf(dto);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const last = (empObj?.person?.lastName || empObj?.person?.last_name || 'empleado').replace(/\s+/g, '_');
      a.href = url;
      a.download = `recibo-liquidacion-final-${last}-${endDate}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast({ title: 'PDF generado', status: 'success', duration: 3000 });
    } catch (e) {
      toast({
        title: 'Error al descargar PDF',
        description: e?.message || 'Intentá de nuevo',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setDownloadingSettlementPdf(false);
    }
  };

  const LineItem = ({ label, amount, highlight, negative }) => (
    <Tr>
      <Td fontSize="sm" borderColor={borderColor}>{label}</Td>
      <Td isNumeric fontWeight={highlight ? '700' : '400'} color={negative ? 'red.500' : highlight ? 'green.600' : 'inherit'} borderColor={borderColor}>
        {fmt(amount)}
      </Td>
    </Tr>
  );

  return (
    <Box>
      <Grid templateColumns={{ base: '1fr', lg: '380px 1fr' }} gap={6}>
        {/* Formulario */}
        <Card p={5}>
          <Heading size="sm" mb={4}>Datos del Egreso</Heading>

          <FormControl mb={3} isRequired>
            <FormLabel fontSize="sm">Empleado</FormLabel>
            <Select size="sm" placeholder="Seleccionar empleado" value={selectedEmp} onChange={(e) => setSelectedEmp(e.target.value)}>
              {employees.map((e) => <option key={e.id} value={e.id}>{e.person?.name} {e.person?.lastName}</option>)}
            </Select>
          </FormControl>

          {empObj && (
            <Alert status="info" mb={3} fontSize="xs" borderRadius="md">
              <AlertIcon />
              Sueldo: <strong style={{ marginLeft: 4 }}>{fmt(empObj.salary)}</strong>
            </Alert>
          )}

          <FormControl mb={3} isRequired>
            <FormLabel fontSize="sm">Fecha de ingreso</FormLabel>
            <Input size="sm" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </FormControl>

          <FormControl mb={3} isRequired>
            <FormLabel fontSize="sm">Fecha de egreso</FormLabel>
            <Input size="sm" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </FormControl>

          <FormControl mb={3} isRequired>
            <FormLabel fontSize="sm">Motivo del egreso</FormLabel>
            <Select size="sm" value={motivo} onChange={(e) => setMotivo(e.target.value)}>
              {MOTIVOS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </Select>
          </FormControl>

          <FormControl mb={3}>
            <FormLabel fontSize="sm">Días trabajados en el mes del cese</FormLabel>
            <NumberInput size="sm" min={0} max={30} value={diasUltimoMes} onChange={(v) => setDiasUltimoMes(v)}>
              <NumberInputField placeholder="Según fechas" />
            </NumberInput>
            <FormHelperText fontSize="xs">
              Base 30 (tope 30 días).
            </FormHelperText>
          </FormControl>

          <Divider mb={3} />
          <Text fontSize="sm" fontWeight="600" mb={2}>Licencia no gozada</Text>
          <FormControl mb={3}>
            <FormLabel fontSize="sm">Días pendientes de años anteriores</FormLabel>
            <NumberInput size="sm" min={0} value={diasLicNGozadaAnteriores} onChange={(v) => setDiasLicNGozadaAnteriores(v)}>
              <NumberInputField />
            </NumberInput>
            <FormHelperText fontSize="xs">
              Días pendientes de años anteriores.
            </FormHelperText>
          </FormControl>
          <Divider mb={3} />


          <Button
            colorScheme="orange"
            w="full"
            leftIcon={<MdCalculate />}
            onClick={calcular}
          >
            Calcular Liquidación Final
          </Button>
        </Card>

        {/* Resultado */}
        <Box>
          {!resultado ? (
            <Flex align="center" justify="center" h="full" minH="200px">
              <Text color="gray.400">Completá los datos y presioná calcular</Text>
            </Flex>
          ) : (
            <Card p={5}>
              <Flex justify="space-between" align="center" flexWrap="wrap" gap={3} mb={4}>
                <Heading size="sm">
                  Liquidación final — {empObj?.person?.name} {empObj?.person?.lastName}
                </Heading>
                <Flex align="center" gap={2}>
                  <Button
                    size="sm"
                    leftIcon={<MdDownload />}
                    variant="outline"
                    colorScheme="blue"
                    onClick={handleDownloadSettlementPdf}
                  >
                    Descargar recibo PDF
                  </Button>
                  <Badge colorScheme={resultado.correspondeIndemnizacion ? 'orange' : 'gray'} fontSize="sm" p={1}>
                    {MOTIVOS.find((m) => m.value === motivo)?.label}
                  </Badge>
                </Flex>
              </Flex>

              <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={3} mb={5}>
                <Stat bg={statGrayBg} p={3} borderRadius="md">
                  <StatLabel fontSize="xs">Antigüedad</StatLabel>
                  <StatNumber fontSize="lg">{resultado.totalYears} años</StatNumber>
                  <StatHelpText>{resultado.totalMonths} meses completos (regla IPD)</StatHelpText>
                </Stat>
                <Stat bg={statBlueBg} p={3} borderRadius="md">
                  <StatLabel fontSize="xs">Partidas gravadas (aportes)</StatLabel>
                  <StatNumber fontSize="lg" color="blue.500">{fmt(resultado.haberesGravadosSim)}</StatNumber>
                  <StatHelpText>sueldo, aguinaldo, licencias</StatHelpText>
                </Stat>
                <Stat bg={statBlueBg} p={3} borderRadius="md">
                  <StatLabel fontSize="xs">Partidas no gravadas</StatLabel>
                  <StatNumber fontSize="lg" color="blue.600">{fmt(resultado.haberesNoGravadosSim)}</StatNumber>
                  <StatHelpText>salario vac., IPD, preaviso</StatHelpText>
                </Stat>
                <Stat bg={statGreenBg} p={3} borderRadius="md">
                  <StatLabel fontSize="xs">Neto estimado</StatLabel>
                  <StatNumber fontSize="lg" color="green.600">{fmt(resultado.netoLiquidacionSim)}</StatNumber>
                  <StatHelpText>bruto {fmt(resultado.brutoTotalSim)} − deducciones</StatHelpText>
                </Stat>
              </SimpleGrid>

              <Table size="sm" variant="simple">
                <Thead bg={headerBg}>
                  <Tr>
                    <Th>Concepto</Th>
                    <Th isNumeric>Monto</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  <LineItem
                    label="Sueldo proporcional del mes"
                    amount={resultado.sueldoProporcional}
                  />
                  <LineItem
                    label="Aguinaldo proporcional"
                    amount={resultado.aguinaldoProporcional}
                  />
                  <LineItem
                    label={`Licencia proporcional año actual (${resultado.diasLicenciaAnoActual} días)`}
                    amount={resultado.licenciaProporcionalAnoActual}
                  />
                  {resultado.hadPriorYears && (
                    <LineItem
                      label={`Licencia no gozada años anteriores (${resultado.diasLicNGozada} días)`}
                      amount={resultado.licenciaNGozadaAnteriores}
                      highlight
                    />
                  )}
                  <LineItem
                    label="Salario vacacional"
                    amount={resultado.salarioVacacionalTotal ?? resultado.salarioVacacionalAnoActual}
                    highlight
                  />
                  {resultado.correspondeIndemnizacion && (
                    <LineItem
                      label={`Indemnización por despido (${resultado.aniosParaIndemnizar} sueldo(s))`}
                      amount={resultado.indemnizacion}
                      highlight
                    />
                  )}
                  {resultado.preaviso > 0 && (
                    <LineItem
                      label="Sustitutivo de preaviso"
                      amount={resultado.preaviso}
                      highlight
                    />
                  )}
                  <Tr bg={headerBg}>
                    <Td fontWeight="700" fontSize="sm">Bruto total haberes</Td>
                    <Td isNumeric fontWeight="700" fontSize="md">{fmt(resultado.brutoTotalSim)}</Td>
                  </Tr>
                </Tbody>
              </Table>

              <Heading size="xs" mt={6} mb={2} textTransform="uppercase" color="gray.500">
                Deducciones del empleado (sobre gravadas)
              </Heading>
              <Table size="sm" variant="simple" mb={2}>
                <Thead bg={headerBg}>
                  <Tr>
                    <Th>Concepto</Th>
                    <Th isNumeric>Monto</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {(resultado.deduccionesSim || []).map((d) => (
                    <LineItem
                      key={d.concept}
                      label={d.concept}
                      amount={d.amount}
                      negative={Number(d.amount) > 0}
                    />
                  ))}
                  <Tr bg={headerBg}>
                    <Td fontWeight="700" fontSize="sm">Total deducciones</Td>
                    <Td isNumeric fontWeight="700" color="red.500" fontSize="md">
                      {fmt(resultado.totalDeduccionesSim)}
                    </Td>
                  </Tr>
                  <Tr>
                    <Td fontWeight="700" fontSize="sm">Neto a pagar (estimado)</Td>
                    <Td isNumeric fontWeight="700" color="green.600" fontSize="md">
                      {fmt(resultado.netoLiquidacionSim)}
                    </Td>
                  </Tr>
                </Tbody>
              </Table>
              <Text fontSize="xs" color="gray.500" mb={2}>
                BPS, FONASA y FRL se calculan solo sobre partidas gravadas. Salario vacacional, indemnización y sustitutivo de preaviso no llevan esos descuentos. El IRPF se determina en la liquidación definitiva del período.
              </Text>

              <Divider my={4} />

              {egresoRegistrado ? (
                <Alert status="success" borderRadius="md">
                  <AlertIcon />
                  <Box>
                    <AlertTitle>Egreso registrado</AlertTitle>
                    <AlertDescription>
                      {empObj?.person?.name} {empObj?.person?.lastName} figura como dado de baja ({MOTIVOS.find((m) => m.value === motivo)?.label}) desde el {endDate}.
                    </AlertDescription>
                  </Box>
                </Alert>
              ) : (
                <Flex justify="flex-end">
                  <Button
                    colorScheme="red"
                    leftIcon={<MdExitToApp />}
                    onClick={handleRegistrarEgreso}
                  >
                    Registrar Egreso en el Sistema
                  </Button>
                </Flex>
              )}
            </Card>
          )}
        </Box>
      </Grid>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────
// Vista principal
// ─────────────────────────────────────────────────────────
export default function LiquidacionesView() {
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const cardBg = useColorModeValue('white', 'navy.800');

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      <Card bg={cardBg} p={0} overflow="hidden">
        <Tabs colorScheme="blue" variant="line" isLazy>
          <TabList px={6} pt={4}>
            <Tab fontWeight="600" fontSize="sm">
              <Icon as={MdPeople} mr={2} />
              Liquidación Masiva
            </Tab>
            <Tab fontWeight="600" fontSize="sm">
              <Icon as={MdPerson} mr={2} />
              Liquidación Individual
            </Tab>
            <Tab fontWeight="600" fontSize="sm">
              <Icon as={MdMonetizationOn} mr={2} />
              Aguinaldo (SAC)
            </Tab>
            <Tab fontWeight="600" fontSize="sm">
              <Icon as={MdExitToApp} mr={2} />
              Despidos / Renuncias
            </Tab>
          </TabList>
          <TabPanels>
            <TabPanel px={6} py={6}><MasivaTab /></TabPanel>
            <TabPanel px={6} py={6}><IndividualTab /></TabPanel>
            <TabPanel px={6} py={6}><AguinaldoTab /></TabPanel>
            <TabPanel px={6} py={6}><DespidosTab /></TabPanel>
          </TabPanels>
        </Tabs>
      </Card>
    </Box>
  );
}
